import Moment from 'moment-timezone';
import { i18n } from '@/plugins/i18n';
import {
  getRecurrenceBreakdown,
  getLabelsAndDatasetsFromRecurrence,
  recurrenceChartOptions,
  findOutliers,
} from '@/utils/vhub-utils';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';


/**
 * Score based on the outlier values for indicators. Base on Tukey https://en.wikipedia.org/wiki/Outlier
 * For each indicators, Shizen calculates the 1st and 3rd quartiles.
 * Then all values outside the range [Q1 - 1,5 * (Q3 - Q1), Q3 + 1,5 * (Q3 - Q1)] is considered an outlier.
 */
export default new VitalityCriterion({
  titleTr: 'VH_I_OUTLIERS',
  key: 'indicatorOutliers',
  weight: 2,

  score: ({ themes }, { start, end }) => {
    // Score is calculated as described above but this criterion may also display a Pareto chart
    // For information about recurrence amongst teams
    const paretoValues = [];

    let nIndicators = 0;
    let nIndicatorsWithOutliers = 0;
    themes.forEach((theme) => {
      theme.indicators.forEach((indic) => {
        if (indic.copyPublic === null && indic.inputValues && !indic.incremental) {
          const values = [];
          const iDate = Moment(start);
          while (!iDate.isAfter(end)) {
            const v = indic.inputValues[iDate.format('YYYY-MM-DD')];
            if (v && v.value !== null) {
              values.push(v.value);
            }
            iDate.add(1, 'd');
          }
          if (values.length > 0) {
            nIndicators++;
            const nOutliers = findOutliers(values).length;
            if (nOutliers > 0) {
              nIndicatorsWithOutliers++;
              paretoValues.push({ libId: indic.libraryId, libName: indic.libName });
            }
          }
        }
      });
    });


    const noValues = nIndicators === 0;
    let score = noValues ? 0 : (nIndicators - nIndicatorsWithOutliers) * 100 / nIndicators;
    const { itemsPerRecurrenceId } = getRecurrenceBreakdown(paretoValues, 'libId', 'libName');

    return {
      score,
      data: {
        score,
        noValues,
        nIndicators,
        nIndicatorsWithOutliers,
        itemsPerRecurrenceId,
      },
    };
  },

  text: ({ score, noValues, nIndicators, nIndicatorsWithOutliers }) => {
    if (noValues) {
      return i18n.t('message.VH_I_NO_VALUES');
    }
    if (nIndicatorsWithOutliers === 0) {
      return i18n.t('message.VH_I_OUTLIERS_NONE_TEXT');
    }
    const perc = nIndicatorsWithOutliers * 100 / nIndicators;
    return i18n.t('message.VH_I_OUTLIERS_TEXT', {
      perc: perc.sToFixed(1),
      nIndicators,
      nIndicatorsWithOutliers,
    });
  },

  chartData(teamGroups) {
    // Display a pareto stacked bar chart
    return getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey);
  },

  chartOptions() {
    return recurrenceChartOptions;
  },

  chartNames() {
    return i18n.t('message.PARETO');
  },

});
